import React from 'react';
import { useSelector } from 'react-redux';

import { NamedLink, ListingCard } from '../../components';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { ASSET_NAME, RECENT_LISTINGS_SECTION_ID } from './LandingPage.duck';

import NewsletterSignup from './NewsletterSignup';
import flourishImage from '../../assets/nextmine-flourish-gold.svg';
import flowerImage from '../../assets/nextmine-flower-gold.svg';

import css from './SectionNextmineLanding.module.css';

const escrowSteps = [
  {
    num: '01',
    title: 'You pay - we hold it',
    text:
      'Your payment goes to Stripe, our secure payment partner. Not a penny reaches the seller yet.',
  },
  {
    num: '02',
    title: 'The seller ships insured',
    text:
      'Your piece is sent with insured, tracked shipping - while your money stays safely on hold.',
  },
  {
    num: '03',
    title: 'You confirm - then we pay',
    text:
      'Only when you confirm your piece has arrived is the seller paid. Not as described? You get your money back.',
  },
];

// Custom letterpress landing section for Nextmine.
const SectionNextmineLanding = () => {
  const listingIds = useSelector(
    state => state.featuredListings?.[ASSET_NAME]?.[RECENT_LISTINGS_SECTION_ID]?.listingIds || []
  );
  const recentListings = useSelector(state => getListingsById(state, listingIds));

  return (
    <div className={css.root}>
      <NamedLink name="HowPaymentsWorkPage" className={css.trustStrip}>
        {/* Kort version på mobil (én linje), fuld sætning fra tablet og op */}
        <span className={css.trustStripShort}>Your payment is protected</span>
        <span className={css.trustStripFull}>
          Secure payments · your payment is protected until you confirm your piece has arrived
        </span>
        <span className={css.trustStripArrow}> →</span>
      </NamedLink>

      {/* hero */}
      <section className={css.hero}>
        <div className={css.eyebrow} style={{ marginBottom: '22px' }}>
          Pre-loved jewelry marketplace
        </div>
        <h1 className={css.heroTitle}>A Circle of Collectors</h1>
        <div className={css.script}>who buy and sell</div>
        <h1 className={css.heroTitle}>fine jewelry</h1>
        <p className={css.lead}>
          Born from the Oldmine community - a trusted circle where treasured pieces pass safely from
          one collector to the next, with secure payments on every sale.
        </p>
        <div className={css.buttons}>
          {/* "The collection" betyder noget andet paa Nextmine nu (en samlers
              egen samling), saa knappen siger hvad den faktisk foerer til. */}
          <NamedLink name="SearchPage" className={css.btnPrimary}>
            Browse available pieces
          </NamedLink>
          <NamedLink name="NewListingPage" className={css.btnSecondary}>
            Sell your jewelry
          </NamedLink>
        </div>
        <div className={css.buttonsIsoRow}>
          <NamedLink name="WantedPage" className={css.btnSecondary}>
            ISO · Wanted
          </NamedLink>
        </div>
      </section>

      {/* flourish */}
      <div className={css.flourishWrap}>
        <img src={flourishImage} alt="" className={css.flourish} />
      </div>

      {/* recently listed */}
      {recentListings.length > 0 ? (
        <section className={css.listed}>
          <div className={css.sectionLabelWrap}>
            <span className={css.eyebrow}>Recently listed</span>
          </div>
          <div className={css.wrap}>
            <div className={css.grid}>
              {recentListings.map(listing => (
                <ListingCard
                  key={listing.id.uuid}
                  listing={listing}
                  className={css.card}
                  aspectRatioClassName={css.cardOval}
                  showAuthorInfo={false}
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* in search of */}
      <section className={css.isoSection}>
        <div className={css.isoInner}>
          <span className={css.eyebrow} style={{ display: 'block' }}>
            ISO - In search of
          </span>
          <h2 className={css.isoTitle}>Looking for a particular piece?</h2>
          <p className={css.isoLead}>
            Sold before you could tap buy on Oldmine? Post the one that got away - a style, an era,
            a stone - and let the circle of collectors surface her, or something that looks just
            like her. We'll email you the moment a match is listed.
          </p>
          <div className={css.buttons}>
            <NamedLink name="WantedPage" className={css.btnPrimary}>
              Browse requests
            </NamedLink>
            <NamedLink name="NewListingPage" className={css.btnSecondary}>
              Post a request
            </NamedLink>
          </div>
        </div>
      </section>

      {/* share my collection */}
      <section className={css.isoSection}>
        <div className={css.isoInner}>
          <span className={css.eyebrow} style={{ display: 'block' }}>
            Share my collection
          </span>
          <h2 className={css.isoTitle}>Show the pieces you'd never sell</h2>
          <p className={css.isoLead}>
            A collection is more than what's for sale. Give your pieces a page of their own - the
            heirloom ring, the first brooch you ever bought, the one you're still hunting a match
            for - and share it with the circle. Someone may fall for a piece you never listed, and
            you decide whether it's for sale.
          </p>
          <div className={css.buttons}>
            <NamedLink name="CollectionsPage" className={css.btnPrimary}>
              Browse collections
            </NamedLink>
            <NamedLink name="MyCollectionPage" className={css.btnSecondary}>
              Share yours
            </NamedLink>
          </div>
        </div>
      </section>

      {/* how payments are protected */}
      <section className={css.escrow}>
        <div className={css.escrowInner}>
          <span className={css.eyebrow} style={{ display: 'block' }}>
            How your payment is protected
          </span>
          <div className={css.escrowGrid}>
            {escrowSteps.map(step => (
              <div key={step.num}>
                <div className={css.stepNum}>{step.num}</div>
                <div className={css.stepTitle}>{step.title}</div>
                <p className={css.stepText}>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* footer cta */}
      <section className={css.footerCta}>
        <img src={flowerImage} alt="" className={css.footerFlower} />
        <div className={css.eyebrow} style={{ margin: '12px 0 6px' }}>
          Join the Nextmine circle
        </div>
        <h2 className={css.footerCtaTitle}>Be first to the next treasure</h2>
        <p className={css.footerLead}>
          Want to know when new pieces are listed on Nextmine? Join our newsletter and you'll be
          first to see them.
        </p>
        <NewsletterSignup />
      </section>
    </div>
  );
};

export default SectionNextmineLanding;
